import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'primereact/button';

const DocumentSidebar = ({ onUpload, chats = [], onSelectChat, selectedChatId, onRenameChat, onDeleteChat }) => {
  const [editingChatId, setEditingChatId] = React.useState(null);
  const [editTitle, setEditTitle] = React.useState('');
  const [deletingChatId, setDeletingChatId] = React.useState(null);

  const startEditing = (chat) => {
    setEditingChatId(chat._id);
    setEditTitle(chat.question || "Untitled Analysis");
    setDeletingChatId(null);
  };

  const handleSaveEdit = (chatId) => {
    if (editTitle.trim() && onRenameChat) {
      onRenameChat(chatId, editTitle.trim());
    }
    setEditingChatId(null);
  };

  const handleDeleteClick = (chatId) => {
    setDeletingChatId(chatId);
    setEditingChatId(null);
  };

  const handleConfirmDelete = (chatId) => {
    if (onDeleteChat) {
      onDeleteChat(chatId);
    }
    setDeletingChatId(null);
  };

  return (
    <div className="document-sidebar">
      <div className="sidebar-header">
        <div className="brand-dot"></div>
        <span>Documents</span>
      </div>

      <div className="upload-container">
        <input
          type="file"
          id="file-upload"
          hidden
          // multiple
          onChange={(e) => {
            onUpload(Array.from(e.target.files || []));
            e.target.value = '';
          }}
          accept=".pdf,.docx,.txt"
        />
        <div
          className="compact-drop-zone"
          onClick={() => document.getElementById('file-upload').click()}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={(e) => {
            e.preventDefault(); e.stopPropagation();
            if (e.dataTransfer.files && e.dataTransfer.files.length) {
              onUpload(Array.from(e.dataTransfer.files));
            }
          }}
        >
          <div className="upload-icon">
            <FontAwesomeIcon icon="upload" />
          </div>
          <div className="upload-text">
            <span>Drop case file</span>
            <small>PDF, DOCX, TXT</small>
          </div>
        </div>
      </div>

      <div className="sidebar-nav">
        <div className="nav-label">RECENT ANALYSIS</div>
        <div className="chat-list">
          {chats.length > 0 ? chats.map(chat => {
            const isEditing = editingChatId === chat._id;
            const isDeleting = deletingChatId === chat._id;

            return (
              <div
                key={chat._id}
                className={`chat-item ${selectedChatId === chat._id ? 'active' : ''}`}
                onClick={() => {
                  if (!isEditing && !isDeleting) {
                    onSelectChat(chat);
                  }
                }}
              >
                {isEditing ? (
                  <div className="chat-edit-container" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      className="chat-edit-input"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveEdit(chat._id);
                        } else if (e.key === 'Escape') {
                          setEditingChatId(null);
                        }
                      }}
                      autoFocus
                    />
                    <div className="edit-actions">
                      <button className="btn-action-check" title="Save" onClick={() => handleSaveEdit(chat._id)}>
                        <FontAwesomeIcon icon="check-circle" />
                      </button>
                      <button className="btn-action-times" title="Cancel" onClick={() => setEditingChatId(null)}>
                        <FontAwesomeIcon icon="times-circle" />
                      </button>
                    </div>
                  </div>
                ) : isDeleting ? (
                  <div className="delete-confirm-container" onClick={(e) => e.stopPropagation()}>
                    <span className="confirm-label">Delete?</span>
                    <div className="edit-actions">
                      <button className="btn-confirm-yes" onClick={() => handleConfirmDelete(chat._id)}>Yes</button>
                      <button className="btn-confirm-no" onClick={() => setDeletingChatId(null)}>No</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="chat-icon">
                      <FontAwesomeIcon icon="file-alt" />
                    </div>
                    <div className="chat-info">
                      <div className="chat-title">{chat.question || "Untitled Analysis"}</div>
                      <div className="chat-date">{new Date(chat.created).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                    </div>
                    <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn-item-action edit-btn"
                        title="Rename analysis"
                        onClick={() => startEditing(chat)}
                      >
                        <FontAwesomeIcon icon="edit" />
                      </button>
                      <button
                        className="btn-item-action delete-btn"
                        title="Delete analysis"
                        onClick={() => handleDeleteClick(chat._id)}
                      >
                        <FontAwesomeIcon icon="trash-alt" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          }) : (
            <div className="empty-state text-center py-4">
              <small className="text-muted">No recent analysis</small>
            </div>
          )}
        </div>
      </div>

      <div className="sidebar-footer">
        <div className="footer-actions">
          <button className="btn-secondary-compact w-100 mb-2">
            <FontAwesomeIcon icon="plus" className="mr-2" /> New Session
          </button>
        </div>
      </div>

      <style jsx>{`
        .document-sidebar {
          width: 20vw;
          height: 100%;
          background: #fdfdfd;
          border-right: 1px solid #f1f5f9;
          display: flex;
          flex-direction: column;
          font-family: 'Inter', -apple-system, system-ui, sans-serif;
        }

        .sidebar-header {
          height: 52px;
          padding: 0 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          font-weight: 600;
          color: #0f172a;
          border-bottom: 1px solid #f1f5f9;
        }

        .brand-dot {
          width: 8px;
          height: 8px;
          background: #2563eb;
          border-radius: 50%;
        }

        .upload-container {
          padding: 16px;
        }

        .compact-drop-zone {
          border: 1px dashed #e2e8f0;
          border-radius: 8px;
          padding: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          background: #f8fafc;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .compact-drop-zone:hover {
          border-color: #2563eb;
          background: #eff6ff;
        }

        .upload-icon {
          width: 32px;
          height: 32px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          font-size: 12px;
        }

        .upload-text {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
        }

        .upload-text span {
          font-size: 12px;
          font-weight: 500;
          color: #1e293b;
        }

        .upload-text small {
          font-size: 10px;
          color: #94a3b8;
        }

        .sidebar-nav {
          flex: 1;
          padding: 12px 8px;
          overflow-y: auto;
        }

        .nav-label {
          padding: 0 12px 8px;
          font-size: 9px;
          font-weight: 700;
          color: #94a3b8;
          letter-spacing: 0.05em;
        }

        .chat-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .chat-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.1s ease;
          position: relative;
        }

        .chat-item:hover {
          background: #f1f5f9;
        }

        .chat-item.active {
          background: #f1f5f9;
          color: #2563eb;
        }

        .chat-item:hover .item-actions {
          display: flex;
        }

        .item-actions {
          display: none;
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          gap: 4px;
          background: linear-gradient(90deg, rgba(241, 245, 249, 0) 0%, #f1f5f9 20%);
          padding-left: 12px;
          height: 100%;
          align-items: center;
        }

        .chat-item.active .item-actions {
          background: linear-gradient(90deg, rgba(241, 245, 249, 0) 0%, #f1f5f9 20%);
        }

        .btn-item-action {
          width: 22px;
          height: 22px;
          border-radius: 4px;
          border: none;
          background: transparent;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 10px;
          transition: all 0.15s;
        }

        .btn-item-action:hover {
          background: #e2e8f0;
          color: #0f172a;
        }

        .btn-item-action.delete-btn:hover {
          background: #fee2e2;
          color: #ef4444;
        }

        .chat-edit-container {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
        }

        .chat-edit-input {
          flex: 1;
          height: 24px;
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          padding: 2px 6px;
          font-size: 11px;
          outline: none;
          background: #ffffff;
          min-width: 0;
        }

        .chat-edit-input:focus {
          border-color: #2563eb;
        }

        .edit-actions {
          display: flex;
          gap: 2px;
        }

        .btn-action-check, .btn-action-times {
          width: 22px;
          height: 22px;
          border-radius: 4px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 11px;
          transition: all 0.15s;
        }

        .btn-action-check {
          background: #eff6ff;
          color: #2563eb;
        }

        .btn-action-check:hover {
          background: #dbeafe;
        }

        .btn-action-times {
          background: #f8fafc;
          color: #64748b;
        }

        .btn-action-times:hover {
          background: #f1f5f9;
          color: #0f172a;
        }

        .delete-confirm-container {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 4px;
          padding: 2px 6px;
          width: 100%;
          justify-content: space-between;
        }

        .confirm-label {
          font-size: 10px;
          font-weight: 600;
          color: #991b1b;
        }

        .btn-confirm-yes, .btn-confirm-no {
          padding: 2px 8px;
          border-radius: 3px;
          border: none;
          font-size: 10px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn-confirm-yes {
          background: #ef4444;
          color: #ffffff;
        }

        .btn-confirm-yes:hover {
          background: #dc2626;
        }

        .btn-confirm-no {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-confirm-no:hover {
          background: #e5e7eb;
        }

        .chat-icon {
          font-size: 12px;
          color: #94a3b8;
        }

        .chat-item.active .chat-icon {
          color: #2563eb;
        }

        .chat-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .chat-title {
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .chat-date {
          font-size: 10px;
          color: #94a3b8;
        }

        .sidebar-footer {
          padding: 12px;
          border-top: 1px solid #f1f5f9;
          background: #ffffff;
        }

        .btn-secondary-compact {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 6px 12px;
          font-size: 11px;
          font-weight: 600;
          color: #475569;
          cursor: pointer;
          transition: all 0.1s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-secondary-compact:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }

        /* Custom Scrollbar */
        .sidebar-nav::-webkit-scrollbar {
          width: 4px;
        }
        .sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-nav::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

export default DocumentSidebar;
